import { useRef } from 'react';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import { Alert, TextV2 } from '@insights-ltd/design-library/components';
import { LinearProgress, styled } from '@mui/material';
import { User } from 'types/types';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import useBulkUpload from './useBulkUpload';

type BulkUploadTeamProps = {
  organisationId: string;
  onBulkAddLearners: (learners: User[]) => void;
};

const StyledDiv = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(spacingSizeMap.M),
  marginBottom: theme.spacing(spacingSizeMap.L),
  '> * + *': {
    marginTop: theme.spacing(spacingSizeMap.XS),
  },
}));

const BulkUploadTeam = ({
  organisationId,
  onBulkAddLearners,
}: BulkUploadTeamProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const hook = useBulkUpload({
    onBulkAddLearners,
    organisationId,
  });

  return (
    <Box
      data-testid="upload-bulk-learners"
      sx={{
        mt: (theme) => theme.spacing(spacingSizeMap.M),
        mb: (theme) => theme.spacing(spacingSizeMap.L),
      }}
    >
      <Box
        sx={{
          mb: (theme) => theme.spacing(spacingSizeMap.S),
        }}
      />
      <Grid container spacing={2} sx={{ flexDirection: 'column' }}>
        <Grid sx={{ flexBasis: 'auto' }} size={12}>
          <TextV2 component="p">
            {t('ui.event-management.team.bulk-upload.guidance')}
          </TextV2>
        </Grid>
        <Grid sx={{ flexBasis: 'auto' }} size={12}>
          <input
            id="bulk-upload"
            ref={inputRef}
            data-testid="bulk-upload"
            name={t('ui.event-management.events.add-learners.tab.upload')}
            style={{ display: 'none' }}
            type="file"
            onChange={(evt) => hook.handleSelectFile(evt.target)}
            accept=".csv"
          />
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="bulk-upload">
            <TextField
              value={hook.fileName}
              variant="outlined"
              disabled
              type="text"
              fullWidth
              placeholder={t(
                'ui.event-management.events.add-learners.placeholder',
              )}
              slotProps={{
                input: {
                  sx: {
                    adornedStart: (theme) => ({
                      paddingLeft: theme.spacing(spacingSizeMap.XS),
                    }),
                  },
                  startAdornment: (
                    <Button
                      variant="contained"
                      component="span"
                      color="primary"
                      sx={(theme) => ({
                        marginRight: theme.spacing(spacingSizeMap.XS),
                      })}
                    >
                      {t('ui.event-management.events.add-learners.upload-file')}
                    </Button>
                  ),
                },
              }}
            />
          </label>
        </Grid>
      </Grid>
      {hook.progress > 0 && (
        <LinearProgress variant="determinate" value={hook.progress} />
      )}
      <StyledDiv>
        {hook.errorMessage && (
          <Alert severity="error">
            <TextV2
              component="p"
              sx={{ color: 'inherit', fontWeight: 'inherit' }}
            >
              {hook.errorMessage}
            </TextV2>
          </Alert>
        )}
        {hook.failedList.length > 0 && (
          <Alert severity="error">
            <TextV2
              component="p"
              sx={{ color: 'inherit', fontWeight: 'inherit' }}
            >
              {t('ui.event-management.team.bulk-upload.error', {
                count: hook.failedList.length,
              })}
            </TextV2>
            <ul>
              {hook.failedList.map((email) => (
                <li key={email}>{email}</li>
              ))}
            </ul>
          </Alert>
        )}
      </StyledDiv>
    </Box>
  );
};
export default BulkUploadTeam;
